
import Vue from "vue"
import { VueConstructor } from "vue/types/umd"
import { EventBus } from "@/plugins/eventBus"
import axios from "axios"
import PositionAutocomplete from '@/components/PositionAutocomplete.vue'
import PositionsAutocompleteMixin from "@/mixins/PositionsAutocompleteMixin.vue"

interface Position {
  position_id: number;
  name: string;
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof PositionsAutocompleteMixin>>).extend({
  name: "ManagePositionsDialog",
  components: {
    PositionAutocomplete
  },
  mixins: [
    PositionsAutocompleteMixin,
  ],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    selectedSW: {
      type: Array as () => string[],
      required: true
    },
    isRemovingPositions: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    loading: false,
    selectedPositions: null as (Position[] & (number | number[])) | null,
  }),
  computed: {
    inputValue: {
      get(): boolean {
        return this.value
      },
      set(val: boolean) {
        this.$emit('input', val)
      }
    },
    selectedWorkersText(): string {
      const count = this.selectedSW.length;
      return count + ' ' + this.$vuetify.lang.t(`$vuetify.${count > 1 ? 'workers' : 'worker'}`);
    }
  },
  methods: {
    checkSelection() {
      // Display snackbar error and hide component if no workers in selection
      if (this.selectedSW.length === 0) {
        EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.empty_selection'), color: 'error' })
        this.inputValue = false
        return false
      }
      return true
    },
    handlePositionSelection(positions: Position[]) {
      this.selectedPositions = positions as (Position[] & (number | number[]));
    },
    async submit() {
      if (!this.selectedPositions || this.selectedPositions.length === 0) {
        return;
      }

      this.loading = true;
      
      try {
        const requestData: {[key: string]: any} = {};
        const actionType = this.isRemovingPositions ? 'remove_positions' : 'add_positions';
        
        // Create request body with position updates for all selected workers
        const positionIds = this.selectedPositions?.map(p => p.position_id) || [];
        this.selectedSW.forEach((workerId: string) => {
          requestData[workerId] = { [actionType]: positionIds };
        });
        
        // Make API request
        await axios.put('/v1/workers', requestData);
        
        // Show success message
        EventBus.$emit('snackbar', {
          message: this.$vuetify.lang.t(`$vuetify.success_${actionType}`),
          color: 'success'
        });
        
        // Close dialog
        this.inputValue = false;
        
        // Emit completed event so parent can refresh data if needed
        this.$emit('completed');
      } catch (error) {
        EventBus.$emit('snackbar', { axiosError: error });
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    inputValue(val: boolean) {
      if (val === true) {
        // Check if selection is empty when dialog opens
        if (!this.checkSelection()) return;
        
        // Reset the selected positions when dialog opens
        this.selectedPositions = null;
        // Reset the positions data in PositionAutocomplete
        this.$nextTick(() => {
          (this.$refs.positionAutocomplete as any)?.resetPositions();
        });
      }
    }
  }
})
